/**
 * Shared sparklink (chat deep-link) contract for Town Square Community and legacy Assistant.
 *
 * Params: message, comma-delimited HTTPS images, comma-delimited image_id, context, spark_id, chat_destination.
 * Upload-modal sparklinks also use display_upload_modal (+ force_uploader_* / modal_header_*).
 * Generated links default to `/community`; `chat_destination=assistant` selects standalone Assistant.
 */

import { hasSparklinkUploadModalParam } from './sparklinkUploadModal';

export const SPARKLINK_PARAM_MESSAGE = 'message';
export const SPARKLINK_PARAM_IMAGES = 'images';
export const SPARKLINK_PARAM_IMAGE_ID = 'image_id';
export const SPARKLINK_PARAM_CONTEXT = 'context';
export const SPARKLINK_PARAM_SPARK_ID = 'spark_id';
export const SPARKLINK_PARAM_CHAT_DESTINATION = 'chat_destination';

/** Default prompt attached to first-upload toast/email sparklinks. */
export const SPARKLINK_FIRST_UPLOAD_MESSAGE = 'Give me an honest critique of this piece.';

/**
 * The sharing user's asfId. Attached by the chat-share producer
 * (`client/src/components/Chat/index.tsx`) and the sparklink producer
 * (`client/src/utils/sharePrompt.ts`) — not by every share-link producer — and
 * `shared/attribution.ts` consumes it, so it lives here as the single source of truth —
 * a rename or typo on one side alone would silently stop all referral capture.
 *
 * Deliberately NOT in SPARKLINK_CHAT_PARAM_KEYS: it is attribution, not chat state, so
 * it must survive `stripConsumedSparklinkParams`.
 */
export const SPARKLINK_PARAM_USER_ID = 'user_id';

/** Query keys owned by the sparklink contract (safe to strip after capture). */
export const SPARKLINK_CHAT_PARAM_KEYS = [
  SPARKLINK_PARAM_MESSAGE,
  SPARKLINK_PARAM_IMAGES,
  SPARKLINK_PARAM_IMAGE_ID,
  SPARKLINK_PARAM_CONTEXT,
  SPARKLINK_PARAM_SPARK_ID,
  SPARKLINK_PARAM_CHAT_DESTINATION,
] as const;

export const MAX_SPARKLINK_MESSAGE_BYTES = 8 * 1024;
export const MAX_SPARKLINK_CONTEXT_BYTES = 8 * 1024;
export const MAX_SPARKLINK_IMAGE_URLS = 5;
export const MAX_SPARKLINK_IMAGE_IDS = 5;
export const MAX_SPARKLINK_SPARK_ID_LENGTH = 64;

const SPARK_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

export type SparklinkDestination = 'community' | 'assistant';

export type SparklinkParams = {
  message?: string;
  images?: string[];
  imageIds?: string[];
  context?: string;
  sparkId?: string;
  destination: SparklinkDestination;
};

export type ParsedSparklink = SparklinkParams & {
  /** Any recognized sparklink param is present (including destination-only). */
  hasRecognizedParam: boolean;
  /** True when message, at least one valid HTTPS image URL, or at least one image id is present. */
  shouldAutoSubmit: boolean;
};

export type BuildSparklinkUrlInput = {
  message?: string | null;
  images?: string[] | string | null;
  imageIds?: string[] | string | null;
  context?: string | null;
  sparkId?: string | null;
  destination?: SparklinkDestination | null;
  /** Optional locale prefix, e.g. `/en`. */
  localePrefix?: string;
  /** Attribution and unrelated params (utm_*, user_id, defer_welcome, etc.). */
  attribution?: Record<string, string | null | undefined>;
};

export type BuildSparklinkUrlOptions = {
  /** Town Square origin for Community-default links. No production default embedded. */
  origin?: string;
  /** Client SPA origin for explicit Assistant destination links. */
  clientAssistantOrigin?: string;
};

function toSearchParams(
  input: URLSearchParams | string | Record<string, string | string[] | undefined | null>
): URLSearchParams {
  if (input instanceof URLSearchParams) return new URLSearchParams(input);
  if (typeof input === 'string') {
    const trimmed = input.startsWith('?') ? input.slice(1) : input;
    return new URLSearchParams(trimmed);
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry != null && entry !== '') params.append(key, entry);
      }
      continue;
    }
    if (value !== '') params.append(key, value);
  }
  return params;
}

function getFirstValue(searchParams: URLSearchParams, key: string): string | null {
  const value = searchParams.get(key);
  if (value === null || value === '') return null;
  return value;
}

export function truncateToMaxBytes(value: string, maxBytes: number): string {
  if (maxBytes <= 0) return '';

  const bytes = new TextEncoder().encode(value);
  if (bytes.length <= maxBytes) return value;

  // Cut on the byte array, not by re-encoding shrinking slices: this also runs
  // server-side on request bodies, where the slice loop was quadratic.
  // 0b10xxxxxx marks a UTF-8 continuation byte, so walk back off any code point
  // that straddles the budget.
  let end = maxBytes;
  while (end > 0 && ((bytes[end] ?? 0) & 0b1100_0000) === 0b1000_0000) {
    end -= 1;
  }

  return new TextDecoder().decode(bytes.subarray(0, end));
}

function parseHttpsImageUrls(raw: string | null): string[] | undefined {
  if (!raw) return undefined;

  const urls: string[] = [];
  for (const part of raw.split(',')) {
    if (urls.length >= MAX_SPARKLINK_IMAGE_URLS) break;
    const candidate = part.trim();
    if (!candidate) continue;

    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== 'https:') continue;
      urls.push(candidate);
    } catch {
      continue;
    }
  }

  return urls.length > 0 ? urls : undefined;
}

const IMAGE_ID_PATTERN = /^\d+$/;

function parseImageIds(raw: string | null): string[] | undefined {
  if (!raw) return undefined;

  const ids: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    if (ids.length >= MAX_SPARKLINK_IMAGE_IDS) break;
    const candidate = part.trim();
    if (!candidate || !IMAGE_ID_PATTERN.test(candidate) || seen.has(candidate)) continue;
    seen.add(candidate);
    ids.push(candidate);
  }

  return ids.length > 0 ? ids : undefined;
}

function parseSparkId(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > MAX_SPARKLINK_SPARK_ID_LENGTH) return undefined;
  if (!SPARK_ID_PATTERN.test(trimmed)) return undefined;
  return trimmed;
}

export function resolveSparklinkDestination(raw: string | null | undefined): SparklinkDestination {
  if (raw?.trim().toLowerCase() === 'assistant') return 'assistant';
  return 'community';
}

export function parseSparklinkParams(
  input: URLSearchParams | string | Record<string, string | string[] | undefined | null>
): ParsedSparklink {
  const searchParams = toSearchParams(input);

  const messageRaw = getFirstValue(searchParams, SPARKLINK_PARAM_MESSAGE);
  const contextRaw = getFirstValue(searchParams, SPARKLINK_PARAM_CONTEXT);
  const images = parseHttpsImageUrls(getFirstValue(searchParams, SPARKLINK_PARAM_IMAGES));
  const imageIds = parseImageIds(getFirstValue(searchParams, SPARKLINK_PARAM_IMAGE_ID));
  const sparkId = parseSparkId(getFirstValue(searchParams, SPARKLINK_PARAM_SPARK_ID));
  const destination = resolveSparklinkDestination(getFirstValue(searchParams, SPARKLINK_PARAM_CHAT_DESTINATION));

  const message = messageRaw ? truncateToMaxBytes(messageRaw, MAX_SPARKLINK_MESSAGE_BYTES) : undefined;
  const context = contextRaw ? truncateToMaxBytes(contextRaw, MAX_SPARKLINK_CONTEXT_BYTES) : undefined;

  const hasDestinationParam = getFirstValue(searchParams, SPARKLINK_PARAM_CHAT_DESTINATION) !== null;
  const hasRecognizedParam = !!(
    message ||
    images?.length ||
    imageIds?.length ||
    context ||
    sparkId ||
    hasDestinationParam ||
    hasSparklinkUploadModalParam(searchParams)
  );
  const shouldAutoSubmit = !!(message || images?.length || imageIds?.length);

  return {
    message,
    images,
    imageIds,
    context,
    sparkId,
    destination,
    hasRecognizedParam,
    shouldAutoSubmit,
  };
}

export function hasSparklinkParams(
  input: URLSearchParams | string | Record<string, string | string[] | undefined | null>
): boolean {
  return parseSparklinkParams(input).hasRecognizedParam || hasSparklinkUploadModalParam(input);
}

export type ParsedSparklinkDestinationUrl = {
  pathname: string;
  searchParams: URLSearchParams;
  parsed: ParsedSparklink;
};

export function parseSparklinkFromDestinationUrl(
  rawUrl: string,
  options: { baseOrigin?: string } = {}
): ParsedSparklinkDestinationUrl | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  const isAbsolute = /^https?:\/\//i.test(trimmed);

  let url: URL;
  try {
    url = isAbsolute ? new URL(trimmed) : new URL(trimmed, options.baseOrigin ?? 'https://arthelper.invalid');
  } catch {
    return null;
  }

  if (isAbsolute && options.baseOrigin) {
    try {
      if (url.origin !== new URL(options.baseOrigin).origin) {
        return null;
      }
    } catch {
      return null;
    }
  }

  const parsed = parseSparklinkParams(url.searchParams);
  if (!parsed.hasRecognizedParam) {
    return null;
  }

  return {
    pathname: url.pathname,
    searchParams: new URLSearchParams(url.searchParams),
    parsed,
  };
}

/** Same-origin community sparklinks can open Arty in-place without a full navigation. */
export function isInterceptableCommunitySparklinkDestination(
  rawUrl: string,
  options: { baseOrigin?: string } = {}
): boolean {
  const resolved = parseSparklinkFromDestinationUrl(rawUrl, options);
  if (!resolved) {
    return false;
  }

  if (resolved.parsed.destination === 'assistant') {
    return false;
  }

  return resolved.pathname.split('/').filter(Boolean).includes('community');
}

export function stripConsumedSparklinkParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  for (const key of SPARKLINK_CHAT_PARAM_KEYS) {
    next.delete(key);
  }
  return next;
}

export function getSparklinkPath(destination: SparklinkDestination, localePrefix = ''): string {
  const prefix = localePrefix.replace(/\/$/, '');
  if (destination === 'assistant') {
    return `${prefix}/assistant`;
  }
  return `${prefix}/community`;
}

function normalizeImagesInput(images: string[] | string | null | undefined): string[] | undefined {
  if (images == null) return undefined;
  if (Array.isArray(images)) {
    return parseHttpsImageUrls(images.join(','));
  }
  return parseHttpsImageUrls(images);
}

function normalizeImageIdsInput(imageIds: string[] | string | null | undefined): string[] | undefined {
  if (imageIds == null) return undefined;
  if (Array.isArray(imageIds)) {
    return parseImageIds(imageIds.join(','));
  }
  return parseImageIds(imageIds);
}

function appendAttribution(search: URLSearchParams, attribution?: Record<string, string | null | undefined>): void {
  if (!attribution) return;
  for (const [key, value] of Object.entries(attribution)) {
    if (value == null || value === '') continue;
    if (search.has(key)) continue;
    search.set(key, value);
  }
}

export function buildSparklinkSearchParams(input: BuildSparklinkUrlInput): URLSearchParams {
  const destination = input.destination ?? 'community';
  const sanitized = parseSparklinkParams({
    message: input.message ?? undefined,
    context: input.context ?? undefined,
    images: normalizeImagesInput(input.images)?.join(','),
    image_id: normalizeImageIdsInput(input.imageIds)?.join(','),
    spark_id: input.sparkId ?? undefined,
    chat_destination: destination === 'assistant' ? 'assistant' : undefined,
  });

  const search = new URLSearchParams();
  appendAttribution(search, input.attribution);

  if (sanitized.message) search.set(SPARKLINK_PARAM_MESSAGE, sanitized.message);
  if (sanitized.images?.length) search.set(SPARKLINK_PARAM_IMAGES, sanitized.images.join(','));
  if (sanitized.imageIds?.length) search.set(SPARKLINK_PARAM_IMAGE_ID, sanitized.imageIds.join(','));
  if (sanitized.context) search.set(SPARKLINK_PARAM_CONTEXT, sanitized.context);
  if (sanitized.sparkId) search.set(SPARKLINK_PARAM_SPARK_ID, sanitized.sparkId);

  return search;
}

export function buildSparklinkUrl(input: BuildSparklinkUrlInput, options: BuildSparklinkUrlOptions = {}): string {
  const destination = input.destination ?? 'community';
  const search = buildSparklinkSearchParams(input);
  const query = search.toString();
  const suffix = query ? `?${query}` : '';

  if (destination === 'assistant') {
    const assistantOrigin = options.clientAssistantOrigin?.replace(/\/$/, '') ?? '';
    const path = getSparklinkPath('assistant', input.localePrefix);
    if (assistantOrigin) {
      return `${assistantOrigin}${path}${suffix}`;
    }
    return `${path}${suffix}`;
  }

  const communityOrigin = options.origin?.replace(/\/$/, '') ?? '';
  const path = getSparklinkPath('community', input.localePrefix);
  if (communityOrigin) {
    return `${communityOrigin}${path}${suffix}`;
  }
  return `${path}${suffix}`;
}

/** Parse stored chat `shareParams` JSON into builder input. */
export function parseShareParamsJson(shareParams: string | null | undefined): BuildSparklinkUrlInput | null {
  if (!shareParams) return null;
  try {
    const parsed = JSON.parse(shareParams) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;

    const imagesRaw = parsed.images;
    let images: string[] | string | undefined;
    if (typeof imagesRaw === 'string') {
      images = imagesRaw;
    } else if (Array.isArray(imagesRaw)) {
      images = imagesRaw.filter((v): v is string => typeof v === 'string');
    }

    const imageIdsRaw = parsed.image_id ?? parsed.imageIds;
    let imageIds: string[] | string | undefined;
    if (typeof imageIdsRaw === 'string') {
      imageIds = imageIdsRaw;
    } else if (Array.isArray(imageIdsRaw)) {
      imageIds = imageIdsRaw.filter((v): v is string => typeof v === 'string');
    }

    const sparkId = firstStringField(parsed.spark_id, parsed.sparkId);
    const destinationRaw = firstStringField(parsed.chat_destination, parsed.chatDestination);

    return {
      message: typeof parsed.message === 'string' ? parsed.message : undefined,
      context: typeof parsed.context === 'string' ? parsed.context : undefined,
      images,
      imageIds,
      sparkId,
      destination: resolveSparklinkDestination(destinationRaw),
    };
  } catch {
    return null;
  }
}

function firstStringField(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      return candidate;
    }
  }

  return undefined;
}
